"use client";
import ProductPage from "@/components/ProductPage";
import { Check } from "lucide-react";
import Section from "@/components/Section";
import ImageText from "@/components/ImageText";
import ImageIndividuell from "./Individuell.jpg";
import ImageResponsive from "./responsive.jpg";
import ImageLanding from "./landing.jpg";
import ImageIntegration from "./integration.jpg";
import ImageSEO from "./seo.jpg";
import ImageWartung from "./wartung.jpg";
import ImageHero from "./hero.jpg";
import ImageTB from "./tb.jpg";

export default function Webdesign() {
  return (
    <ProductPage
      productName={"Webdesign & Webentwicklung"}
      hero={{
        title: "Modernes Webdesign & individuelle Weblösungen",
        subtitle:
          "Moderne Webseiten, responsive Designs und maßgeschneiderte Weblösungen – für einen professionellen Online-Auftritt.",
        buttonLeft: {
          title: "Unverbindliche Anfrage",
          href: "/support",
        },
        buttonRight: {
          title: "Kontakt aufnehmen",
          href: "/kontakt",
        },
        image: ImageTB,
        imageAlt: "Webdesign und Webentwicklung",
      }}
      highlight={{
        image: ImageHero,
        bullets: [
          "Unser Team kombiniert Design, Benutzerfreundlichkeit und Performance – damit Ihre Website nicht nur gut aussieht, sondern auch messbare Ergebnisse liefert.",
          "Wir setzen auf moderne Webtechnologien, optimierte Ladezeiten und SEO-Basics, um Ihre Seite technisch und inhaltlich optimal aufzustellen.",
          "Individuelle Lösungen statt Baukasten – wir entwickeln Ihre Website genau nach Ihren Anforderungen und Ihrem Markenauftritt.",
          "Persönlicher Support und schnelle Umsetzung – von der ersten Idee bis zur fertigen Website haben Sie einen festen Ansprechpartner.",
        ],
      }}
      beforeVideo={
        <Section className="bg-background text-foreground py-12 md:py-20">
          <ImageText
            imagePosition="right"
            imageSlot={
              <div className="relative overflow-hidden mt-15 rounded-2xl border border-black/10 bg-black shadow-sm h-100">
                <video className="h-full w-full object-cover" controls>
                  <source src="/VideoFürWebseite.mp4" type="video/mp4" />
                </video>
              </div>
            }
            title="Websites, die überzeugen – technisch und visuell"
          >
            <p className="text-slate-700 leading-relaxed">
              Ihre Website ist oft der erste Eindruck Ihres Unternehmens – wir
              sorgen dafür, dass er überzeugt. Mit einem klaren Design, moderner
              Technik und optimaler Nutzerführung entwickeln wir Webseiten, die
              funktionieren und begeistern.
            </p>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Von der Landingpage bis zur komplexen Unternehmenswebsite setzen
              wir auf individuelle Konzepte, die Ihre Marke stärken und Ihre
              Zielgruppe ansprechen – responsive, schnell und DSGVO-konform.
            </p>

            <ul className="mt-4 space-y-2">
              <li className="flex items-start gap-3 text-slate-700">
                <Check className="shrink-0 mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Individuelles Design:</strong> Maßgeschneidert nach
                  Ihrer Marke und Corporate Identity.
                </span>
              </li>
              <li className="flex items-start gap-3 text-slate-700">
                <Check className="shrink-0 mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Technisch stark:</strong> Optimierte Performance,
                  SEO-Basics und DSGVO-konforme Umsetzung.
                </span>
              </li>
              <li className="flex items-start gap-3 text-slate-700">
                <Check className="shrink-0 mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Langfristige Betreuung:</strong> Wartung, Updates und
                  Hosting aus einer Hand.
                </span>
              </li>
            </ul>
          </ImageText>
        </Section>
      }
      features={[
        {
          src: ImageIndividuell,
          alt: "Individuelles Webdesign nach Corporate Design",
          title: "Individuelles Design nach CI/CD",
          desc: "Wir gestalten jedes Layout individuell – abgestimmt auf Ihre Marke, Ihr Corporate Design und Ihre Zielgruppe.",
        },
        {
          src: ImageResponsive,
          alt: "Responsive Webseiten für alle Geräte",
          title: "Responsive & mobiloptimiert",
          desc: "Ihre Website funktioniert auf allen Geräten – egal ob Desktop, Tablet oder Smartphone.",
        },
        {
          src: ImageLanding,
          alt: "Landingpages, Onepager und Multi-Page Websites",
          title: "Landingpages, Onepager & Multi-Page Websites",
          desc: "Von kompakten Kampagnenseiten bis hin zu umfangreichen Firmenwebseiten – wir realisieren jede Projektgröße.",
        },
        {
          src: ImageIntegration,
          alt: "Integration von Medien und Animationen im Webdesign",
          title: "Integration von Medien & Animationen",
          desc: "Bilder, Videos, Icons und Animationen – wir sorgen für eine lebendige und ansprechende Webpräsenz.",
        },
        {
          src: ImageSEO,
          alt: "SEO und Performance Optimierung",
          title: "Technik, SEO & Performance",
          desc: "CMS-Integration, schnelle Ladezeiten, Barrierefreiheit und DSGVO-konforme Umsetzung – alles aus einer Hand.",
        },
        {
          src: ImageWartung,
          alt: "Wartung und Pflege von Webseiten nach Launch",
          title: "Wartung & Updates",
          desc: "Wir übernehmen Pflege, Hosting und regelmäßige Updates – damit Ihre Website langfristig sicher und aktuell bleibt.",
        },
      ]}
      faqs={[
        {
          q: "Was unterscheidet Ihr Webdesign von Baukasten-Lösungen?",
          a: "Unsere Webseiten werden individuell entworfen und technisch maßgeschneidert. So entsteht ein einzigartiger Online-Auftritt, der Ihre Marke optimal repräsentiert – ohne Einschränkungen durch Vorlagen.",
        },
        {
          q: "Mit welchen Systemen arbeiten Sie?",
          a: "Wir arbeiten je nach Projekt mit WordPress, Headless-CMS oder individuellen Eigenentwicklungen. Dabei achten wir immer auf einfache Pflege und Erweiterbarkeit.",
        },
        {
          q: "Sind Ihre Webseiten mobil optimiert?",
          a: "Ja. Responsives Design ist Standard bei uns – Ihre Website wird auf allen Endgeräten optimal dargestellt.",
        },
        {
          q: "Übernehmen Sie auch die Suchmaschinenoptimierung (SEO)?",
          a: "Wir führen eine grundlegende technische und inhaltliche SEO-Optimierung durch – inklusive sauberer Struktur, Metadaten und schneller Ladezeiten.",
        },
        {
          q: "Können bestehende Webseiten modernisiert werden?",
          a: "Natürlich. Wir überarbeiten bestehende Seiten technisch und optisch und bringen sie auf den neusten Stand. Unser Team optimiert die Performance von Webseiten für eine besseres Nutzererlebnis.",
        },
        {
          q: "Bieten Sie auch Wartung und Pflege an?",
          a: "Ja. Wir kümmern uns nach dem Launch um Updates, Backups, Sicherheitschecks und inhaltliche Anpassungen – damit Ihre Website dauerhaft zuverlässig läuft.",
        },
        {
          q: "Wie lange dauert die Erstellung einer Website?",
          a: "Je nach Umfang zwischen 4 und 10 Wochen – vom Design über die Entwicklung bis zum Launch.",
        },
        {
          q: "Was kostet eine professionelle Website?",
          a: "Die Kosten hängen von Funktionsumfang, Design und gewünschten Features ab. Nach einem Beratungsgespräch erhalten Sie ein transparentes Angebot.",
        },
        {
          q: "Bieten Sie auch Hosting und Domain-Service an?",
          a: "Ja. Wir unterstützen bei Domainregistrierung, E-Mail-Einrichtung, Hosting und allen technischen Fragen rund um Ihre Website.",
        },
        {
          q: "Welche typischen Projekte realisieren Sie?",
          a: "Wir entwickeln Firmenwebseiten, Landingpages, Marketing-Sites und individuelle Weblösungen – immer mit Fokus auf Design, Funktion und Performance.",
        },
      ]}
    />
  );
}
